import { FC } from 'react'
import styles from '../TicketLanding.module.css'
import classNames from 'classnames'
import { motion, Variants } from 'framer-motion'
import { useMediaQuery } from 'react-responsive'
import { Text } from '@/atoms/Text'
import { EmblaCarousel, EmblaSlide } from '@/molecules/EmblaCarousel'
import { useTranslate } from '@/utils/translate/translate-client'
import { ComingSoonReleaseCard } from '@/views/TicketsLandingView/NewTicketsLandingView/components/ComingSoonReleaseCard'
import { ComingSoonTheatricalRelease } from '../types'

const sectionVariants: Variants = {
  hidden: {
    opacity: 0,
    y: 20,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.3,
      ease: 'easeOut',
      staggerChildren: 0.1,
    },
  },
}

const slideVariants: Variants = {
  hidden: {
    opacity: 0,
    y: 20,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: 'easeOut',
    },
  },
}

const desktopSlideVariants: Variants = {
  hidden: {
    opacity: 0,
    x: 20,
  },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.4,
      ease: 'easeOut',
    },
  },
}

interface Props {
  releases: ComingSoonTheatricalRelease[]
  className?: string
}

export const ComingSoonReleases: FC<Props> = ({ releases, className }) => {
  const { t } = useTranslate('tickets')
  const isDesktop = useMediaQuery({ minWidth: 768 })

  return (
    <motion.section
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
      variants={sectionVariants}
      className={classNames('flex flex-col gap-4 md:gap-10 z-10 relative', className)}
    >
      <Text
        className={classNames('photon-title-md md:photon-heading-lg xl:photon-heading-xl', styles.layoutContainer)}
        weight="bold"
        color="white"
      >
        {t('comingSoonToTheatersTitle', 'Coming Soon to Theaters')}
      </Text>

      <EmblaCarousel
        includeArrows
        className="relative "
        rightArrowClassname="hidden md:block top-[95px] lg:top-[120px]"
        leftArrowClassname="hidden md:block top-[95px] lg:top-[120px]"
        options={{
          active: false,
          breakpoints: {
            '(min-width: 768px)': {
              active: true,
              dragFree: true,
              align: 'start',
              containScroll: 'trimSnaps',
              slidesToScroll: 1,
            },
          },
        }}
        containerClassName={classNames('flex flex-col gap-10 md:gap-6 lg:gap-8 md:flex-row', styles.layoutContainer)}
      >
        {releases.map((release) => {
          return (
            <EmblaSlide fadeOutOfView className="w-full md:max-w-[320px] xl:max-w-[420px]" key={release.theatricalSlug}>
              <motion.div variants={isDesktop ? desktopSlideVariants : slideVariants}>
                <ComingSoonReleaseCard release={release} />
              </motion.div>
            </EmblaSlide>
          )
        })}
      </EmblaCarousel>
    </motion.section>
  )
}
